import {
  getAmountNeededToReachBulkDiscountTier,
  getBulkDiscountTier,
  getNextBulkDiscountTier,
  getTotalSavingsFromBulkDiscount,
} from 'common/BulkDiscountUtils';
import { formatCurrency } from 'common/FormatUtilities';
import { isNil } from 'lodash';
import { getPercentFromDecimal } from 'src/shared/utils/NumberUtils';
import { match } from 'ts-pattern';
import {
  DEFAULT_FREE_DELIVERY_THRESHOLD,
  REWARDS_DISCOUNTED_DELIVERY_TEXT,
  REWARDS_MAX_REACHED_TEXT,
  REWARDS_PERCENT_OFF_TEXT,
} from '../consts';
import { BulkDiscountProps } from '../types';

export const useBulkDiscount = (props: BulkDiscountProps) => {
  const {
    deliveryFee = 0,
    discountedDeliveryFee = 0,
    discountedDeliveryThreshold = DEFAULT_FREE_DELIVERY_THRESHOLD,
    hasFreeDeliveryPromo = false,
    hasItemsInCart = true,
    promoValue = 0,
    tiers = [],
    showFreeDelivery = true,
    total,
    ...otherProps
  } = props;
  const maxValue = tiers[tiers.length - 1]?.minSubtotal ?? 0;
  const firstTier = tiers[0] ?? { minSubtotal: 1 };
  const tiersWithDelivery = showFreeDelivery
    ? [
        {
          minSubtotal: hasFreeDeliveryPromo ? -1 : discountedDeliveryThreshold,
          maxSubtotal: firstTier.minSubtotal - 1,
          discountRate: 0,
        },
        ...tiers,
      ]
    : tiers;

  const formattedTiers = tiersWithDelivery.map(({ minSubtotal }) => ({
    percent: getPercentFromDecimal(minSubtotal / maxValue),
  }));
  const percent = Math.min(getPercentFromDecimal(total / maxValue), 100);

  const nextTier = getNextBulkDiscountTier(tiersWithDelivery, total);
  const currentTier = getBulkDiscountTier(tiersWithDelivery, total);

  const isDiscountedDeliveryReached = showFreeDelivery && total >= discountedDeliveryThreshold;
  const isDiscountedDeliveryReward =
    showFreeDelivery && nextTier ? nextTier.minSubtotal === discountedDeliveryThreshold : false;
  const isMaxRewardsReached = !nextTier && isNil(currentTier?.maxSubtotal);
  const isDiscountReward = !isDiscountedDeliveryReward && !isMaxRewardsReached;

  const discountPercent = getPercentFromDecimal(nextTier?.discountRate ?? 0);
  const rewardsText = match({ isDiscountedDeliveryReward, isMaxRewardsReached })
    .with(
      { isDiscountedDeliveryReward: true },
      () =>
        `${
          discountedDeliveryFee === 0 ? 'Free' : formatCurrency(discountedDeliveryFee)
        }  ${REWARDS_DISCOUNTED_DELIVERY_TEXT}`
    )
    .with({ isMaxRewardsReached: true }, () => REWARDS_MAX_REACHED_TEXT)
    .otherwise(() => `${discountPercent.toFixed(0)}% ${REWARDS_PERCENT_OFF_TEXT}`);

  const totalSavings = getTotalSavingsFromBulkDiscount(currentTier, total) + promoValue;
  const amountForDiscount = getAmountNeededToReachBulkDiscountTier(nextTier, total);

  return {
    ...otherProps,
    amountForDiscount,
    breakdownProps: {
      ...props,
      hasFreeDeliveryPromo,
      showFreeDelivery,
      tiers: tiersWithDelivery,
      totalSavings,
    },
    currentTier,
    deliveryFee,
    discountedDeliveryFee,
    nextTier,
    discountedDeliveryThreshold,
    isDiscountReward,
    isDiscountedDeliveryReached,
    isMaxRewardsReached,
    hasItemsInCart,
    percent,
    rewardsText,
    showFreeDelivery,
    tiers: formattedTiers,
    total,
    totalSavings,
  };
};
